import { CompanyTag } from "@/components/domain/CompanyTag";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Plus, Search } from "lucide-react";
import { type Paginated, type SampleOrderDetailDto, type SampleOrderSummaryDto } from "@hisense/shared";
import { apiFetch, ApiClientError } from "@/lib/api";
import { ExportButton } from "@/components/domain/ExportButton";
import { PageBody } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { EmptyTableRow, ListTableFrame, LIST_PAGE_BODY } from "@/components/ui/ListTable";
import { SortTh, Td, Th, Tr } from "@/components/ui/Table";
import { Stack } from "@/components/ui/Stack";
import { ImagePreviewModal, ImageThumbButton } from "@/components/ui/ImageThumbButton";
import { ErrorState } from "@/components/ui/States";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import { useSampleOrders, useSuppliers, useCustomers } from "@/api/hooks";
import { formatDate } from "@/domain/datetime";
import { translateStatus } from "@/domain/i18n";
import { useClampListPage, useListQueryState, useParamInput } from "@/lib/listQuery";
import { RowPrintButton } from "@/components/domain/RowPrintButton";
import { SamplePrintPreview } from "@/components/samples/SamplePrintPreview";
import { Select } from "@/components/ui/Select";

const STATUS_TONE: Record<string, "neutral" | "info" | "warn" | "ok" | "danger"> = {
  Draft: "neutral",
  Sent: "info",
  Received: "info",
  Testing: "warn",
  Approved: "ok",
  Rejected: "danger",
  Completed: "ok",
};

export function SampleOrdersPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const list = useListQueryState();
  const teamInput = useParamInput(list, "team");
  const {search}=useLocation();
  const [printOrder,setPrintOrder]=useState<SampleOrderDetailDto|null>(null);
  const suppliers=useSuppliers({});const customers=useCustomers({});
  const orderType=list.searchParams.get("orderType")??"";
  const partyId=list.searchParams.get("partyId")??"";
  const team=list.searchParams.get("team")??"";
  const from=list.searchParams.get("fromDate")??"";const to=list.searchParams.get("toDate")??"";
  const day=(v:string,end=false)=>v?new Date(`${v}T${end?"23:59:59.999":"00:00:00.000"}Z`).getTime():undefined;
  const sortProps={activeKey:list.sort,dir:list.dir,onToggle:list.toggleSort};
  const statusFilter = list.searchParams.get("status") ?? "";
  const { data, isLoading, isFetching, error, refetch } = useSampleOrders({
    q: list.q.trim() || undefined,
    status: statusFilter || undefined, orderType:orderType||undefined, partyId:partyId||undefined, team:team||undefined, from:day(from), to:day(to,true), sort:list.sort ? (list.dir==="asc"?list.sort:`-${list.sort}`):undefined,
    page: list.page,
    pageSize: list.pageSize,
  });
  useClampListPage(data?.total, list.clampPage);
  const forbidden = error instanceof ApiClientError && error.status === 403;
  const canCreate = user ? can("records:create") : false;
  // Máy chủ đã lọc rồi — KHÔNG lọc lại ở đây, vì lọc trên mảng đã tải chỉ áp
  // được cho đúng trang hiện tại và làm phân trang nói dối.
  const shown = data?.data ?? [];
  // Chip "Tất cả" phải là tổng CHƯA lọc; `data.total` bây giờ là tổng ĐÃ lọc,
  // dùng nó thì đang lọc Approved mà chip Tất cả cũng hiện đúng số Approved.
  const totalAll = (data?.statusCounts ?? []).reduce((a, b) => a + b.count, 0);
  const [preview, setPreview] = useState<{ url: string; title: string; caption?: string | null } | null>(null);

  return (
    <PageBody className={LIST_PAGE_BODY}>
      {printOrder&&<SamplePrintPreview order={printOrder} onClose={()=>setPrintOrder(null)}/>}
      {preview && (
        <ImagePreviewModal
          eyebrow={t("samples.controlSample")}
          title={preview.title}
          caption={preview.caption}
          src={preview.url}
          onClose={() => setPreview(null)}
        />
      )}
      {/* Dải trạng thái vừa là thống kê vừa là bộ lọc — hệ cũ có số liệu nhanh
          Testing/Approved nhưng bấm vào không làm gì. */}
      {(data?.statusCounts?.length ?? 0) > 0 && (
        <div className="mb-2 flex shrink-0 flex-wrap gap-1.5">
          {[{ status: "", count: totalAll }, ...data!.statusCounts].map((sc) => (
            <button
              key={sc.status || "all"}
              type="button"
              aria-pressed={statusFilter===sc.status}
              onClick={() => list.updateParam("status", sc.status || null)}
              className={
                "rounded-sm border px-2.5 py-1 text-xs transition-colors " +
                (statusFilter === sc.status
                  ? "border-brand bg-brand-tint text-brand-ink"
                  : "border-line bg-surface text-ink-soft hover:border-brand")
              }
            >
              {sc.status ? translateStatus(t, sc.status) : t("common.all")}{" "}
              <span className="font-mono tabular font-semibold">{sc.count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Input
          {...list.searchInput}
          leading={<Search />}
          placeholder={t("common.search")}
          aria-label={t("common.search")}
          className="w-full max-w-xs"
        />
        <div className="ml-auto">
          <ExportButton<SampleOrderSummaryDto>
            name="sample-orders"
            title={t("nav.sampleOrders")}
            rows={shown}
            total={data?.total}
            disabled={isFetching}
            errorKey="samples.errorOrders"
            fetchPage={(page, pageSize, signal) =>
              apiFetch<Paginated<SampleOrderSummaryDto>>("/sample-orders", {
                query: {
                  q: list.q.trim() || undefined,
                  status: statusFilter || undefined,
                  orderType: orderType || undefined,
                  partyId: partyId || undefined,
                  team: team || undefined,
                  from: day(from),
                  to: day(to, true),
                  sort: list.sort ? (list.dir === "asc" ? list.sort : `-${list.sort}`) : undefined,
                  page,
                  pageSize,
                },
                signal,
              })
            }
            columns={[
              { header: t("samples.orderNo"), value: (r) => r.sampleOrderNumber },
              { header: t("common.type"), value: (r) => translateStatus(t, r.orderType) },
              { header: t("common.status"), value: (r) => translateStatus(t, r.status) },
              // Một đơn mẫu chỉ có MỘT bên: từ nhà cung cấp, hoặc gửi cho
              // khách. Bảng gộp làm một cột "Đối tác"; tệp giữ nguyên vậy để
              // khớp màn hình, và ghi rõ loại ở cột bên cạnh.
              { header: t("samples.party"), value: (r) => r.supplierName ?? r.customerName },
              { header: t("samples.firstProduct"), value: (r) => r.firstProductNo },
              { header: t("common.subRef"), value: (r) => r.firstProductSubRef ?? null },
              { header: t("products.description"), value: (r) => r.firstProductDescription },
              { header: t("common.date"), value: (r) => formatDate(r.orderDate) },
              { header: t("samples.orderDelivery"), value: (r) => formatDate(r.deliveryDate) },
              { header: t("common.items"), value: (r) => r.itemCount },
              { header: t("samples.attachments"), value: (r) => r.attachmentCount },
              { header: t("samples.controlSample"), value: (r) => t(r.controlSample ? "common.yes" : "common.no") },
              { header: t("samples.courier"), value: (r) => r.courier },
              { header: t("samples.trackingNo"), value: (r) => r.trackingNo },
              { header: t("samples.shippedAt"), value: (r) => formatDate(r.shippedAt) },
              { header: t("common.createdBy"), value: (r) => r.createdByName },
            ]}
          />
        </div>
        {canCreate && (
          <Button size="sm" onClick={() => navigate(`/sample-orders/new${search}`)}>
            <Plus className="size-4" /> {t("samples.newOrder")}
          </Button>
        )}
      </div>
      <div className="mt-3 flex shrink-0 flex-wrap items-end gap-3">
        <div className="w-40"><FieldLabel htmlFor="sample-filter-type">{t("samples.orderType")}</FieldLabel><Select id="sample-filter-type" containerClassName="w-full" value={orderType} onChange={e=>list.updateParam("orderType",e.target.value)}><option value="">{t("common.all")}</option><option value="From Supplier">{t("samples.fromSupplier")}</option><option value="To Customer">{t("samples.toCustomer")}</option></Select></div>
        <div className="w-56"><FieldLabel htmlFor="sample-filter-party">{t("samples.party")}</FieldLabel><Select id="sample-filter-party" containerClassName="w-full" value={partyId} onChange={e=>list.updateParam("partyId",e.target.value)}><option value="">{t("common.all")}</option>{suppliers.data?.data.map(p=><option key={p.id} value={p.id}>{p.companyName}</option>)}{customers.data?.data.map(p=><option key={p.id} value={p.id}>{p.companyName}</option>)}</Select></div>
        <div className="w-36"><FieldLabel htmlFor="sample-filter-team">{t("samples.team")}</FieldLabel><Input id="sample-filter-team" {...teamInput}/></div>
        <div><FieldLabel htmlFor="sample-from">{t("sampleWorkspace.deliveryFrom")}</FieldLabel><Input id="sample-from" type="date" max={to||undefined} value={from} onChange={e=>list.updateParam("fromDate",e.target.value)}/></div>
        <div><FieldLabel htmlFor="sample-to">{t("sampleWorkspace.deliveryTo")}</FieldLabel><Input id="sample-to" type="date" min={from||undefined} value={to} onChange={e=>list.updateParam("toDate",e.target.value)}/></div>
        {(list.q||orderType||partyId||team||from||to||statusFilter)&&<Button size="sm" variant="ghost" onClick={()=>navigate("/sample-orders",{replace:true})}>{t("sampleWorkspace.clearFilters")}</Button>}
      </div>
      <ListTableFrame
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        errorMessage={t("samples.errorOrders")}
        errorNode={
          forbidden ? (
            <ErrorState message={t("errors.forbidden")} />
          ) : (
            <ErrorState message={t("samples.errorOrders")} onRetry={() => refetch()} />
          )
        }
        onRetry={() => refetch()}
        page={data?.page ?? list.page}
        pageSize={data?.pageSize ?? list.pageSize}
        total={data?.total ?? 0}
        onPageChange={list.updatePage}
        onPageSizeChange={list.updatePageSize}
      >
        <thead>
          <tr>
            <Th>#</Th>
            <SortTh pin sortKey="sampleOrderNumber" {...sortProps}>{t("samples.orderNo")}</SortTh>
            <Th>{t("common.type")}</Th>
            <Th align="center">{t("common.status")}</Th>
            <Th>{t("samples.party")}</Th>
            <Th>{t("samples.firstProduct")}</Th>
            <SortTh sortKey="deliveryDate" {...sortProps}>{t("samples.orderDelivery")}</SortTh>
            <Th align="center">{t("common.items")}</Th>
            <Th align="center">{t("samples.attachments")}</Th>
            <Th align="center">{t("samples.controlSample")}</Th>
            <Th>{t("samples.labTest")}</Th>
            <Th>{t("common.createdBy")}</Th>
            <Th pinRight align="right">{t("common.actions")}</Th>
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 && <EmptyTableRow colSpan={13}>{t("common.noMatches")}</EmptyTableRow>}
          {shown.map((r,index) => (
            <Tr key={r.id} interactive className="group">
              <Td className="font-mono tabular text-ink-faint">{(list.page-1)*list.pageSize+index+1}</Td>
              <Td pin>
                <Link
                  to={`/sample-orders/${r.id}${search}`}
                  className="flex items-center gap-2 font-mono tabular font-medium text-brand group-hover:underline"
                >
                  {r.sampleOrderNumber}
                </Link>
              </Td>
              <Td className="text-xs text-ink-soft">
                {r.orderType === "From Supplier"
                  ? t("samples.fromSupplier")
                  : r.orderType === "To Customer"
                    ? t("samples.toCustomer")
                    : r.orderType}
              </Td>
              <Td align="center">
                <Badge tone={STATUS_TONE[r.status] ?? "neutral"}>{translateStatus(t, r.status)}</Badge>
              </Td>
              <Td className="min-w-48 max-w-72 whitespace-normal text-ink">{r.orderType === "To Customer" && <CompanyTag region={r.customerRegion} className="mr-1.5" />}{(r.orderType === "To Customer" ? r.customerName : r.supplierName) ?? "—"}</Td>
              {/* Đơn này làm mẫu CÁI GÌ. Danh sách chỉ có số phiếu và tên đối
                  tác thì phải mở từng đơn mới biết. */}
              <Td>
                {r.firstProductNo || r.firstProductDescription ? (
                  <Stack
                    monoTop
                    top={[r.firstProductNo,r.firstProductSubRef].filter(Boolean).join(" / ") || "—"}
                    bottom={
                      r.itemCount > 1
                        ? `${r.firstProductDescription ?? ""} ${t("samples.andMore", { count: r.itemCount - 1 })}`.trim()
                        : r.firstProductDescription
                    }
                  />
                ) : (
                  <span className="text-ink-faint">—</span>
                )}
              </Td>
              <Td>
                <Stack
                  monoTop
                  top={formatDate(r.orderDate)}
                  bottom={r.deliveryDate ? formatDate(r.deliveryDate) : null}
                />
              </Td>
              <Td align="center" className="font-mono tabular text-ink-soft">
                {r.itemCount}
              </Td>
              <Td
                align="center"
                className={"font-mono tabular " + (r.attachmentCount > 0 ? "text-ink-soft" : "text-ink-faint")}
              >
                {r.attachmentCount}
              </Td>
              <Td align="center">
                {/* Ảnh thật thay cho dấu tick: người dùng vào đây để NHÌN mẫu
                    đối chứng, một dấu tick chỉ nói là "có ảnh ở đâu đó". */}
                {r.controlSampleUrl ? (
                  <span className="inline-flex">
                    <ImageThumbButton
                      src={r.controlSampleUrl}
                      label={`${t("samples.controlSample")}: ${r.sampleOrderNumber}`}
                      onOpen={() =>
                        setPreview({
                          url: r.controlSampleUrl!,
                          title: r.sampleOrderNumber,
                          caption: r.firstProductDescription ?? r.firstProductNo,
                        })
                      }
                    />
                  </span>
                ) : r.controlSample ? (
                  <Check className="inline-block size-3.5 text-ok" aria-label={t("samples.controlSample")} />
                ) : (
                  <span className="text-ink-faint">—</span>
                )}
              </Td>
              <Td className="text-xs text-ink-soft">{r.labTestRequest ?? "—"}</Td>
              <Td className="text-xs text-ink-soft">{r.createdByName ?? "—"}</Td>
              <Td pinRight align="right">
                <RowPrintButton
                  path={`/sample-orders/${r.id}`}
                  print={(order:SampleOrderDetailDto)=>setPrintOrder(order)}
                  label={`${t("sampleWorkspace.printPreview")}: ${r.sampleOrderNumber}`}
                />
              </Td>
            </Tr>
          ))}
        </tbody>
      </ListTableFrame>
    </PageBody>
  );
}
